import moment, { Moment } from "moment";
import * as React from "react";
import { useIntl } from "react-intl";
import styled from "styled-components";
import {Routine} from "redux-saga-routines";
import {Action, ActionFunctionAny} from "redux-actions";
import { Checkbox } from "@Elements/CheckboxFormik";
import ArrowDown from "@Elements/Icon/Icons/arrows/ArrowDown";
import ArrowUp from "@Elements/Icon/Icons/arrows/ArrowUp";
import { mapDataForExport, mapHeaderForExport, transactionEventFilterValues, transactionTimeFilterValues } from "./utils";
import { AppState } from "@Safemate/Store/types";
import { TransactionEventFilterEnum } from "./TransactionEnum";
import { connect } from "react-redux";
import { setDate, SetDate, SetDetails, setDetails, SetFilter, setFilter, setShowPosition, SetShowPosition } from "./Store/actions";
import { Collapse } from "react-collapse";
import { Radio } from "@Elements/RadioFormik";
import { getTransactions } from "./Store/routines";
import { Button } from "react-bootstrap";
import TableExport from "@Elements/Export/TableExport";
import { Transaction } from "@Safemate/Model/Transaction";
import { isStationary } from "@Safemate/DeviceHardwareEnum";

const FilterContainer = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  padding-top: 190px;
  height: 100vh;
  width: 225px;
  background-color: ${props => props.theme.colors.statusBarBackground};
`;

const FilterContent = styled.div`
  flex-direction: column;
  display: flex;
  justify-content: center;
  padding: 0px 10px 0px;
`;

const FilterItem = styled.div<{hideBorder?: boolean}>`
  margin-top: 10px;
  ${props => !props.hideBorder && `border-top: ${props.theme.colors.border}`};

  .ReactCollapse--collapse {
    transition: height 500ms;
  }

`;

const StyledDateInput = styled.input`
  background-color: ${props => props.theme.colors.backgroundPrimary};
  color: ${props => props.theme.colors.textPrimary};
  width: inherit !important;
  border: ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.colors.borderRadius};
  padding: 9px;
`;

const ItemHeader = styled.div`
  cursor: pointer;
  padding: 10px 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-transform: capitalize;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;


const Filter = () => {
  
  return(
    <FilterContainer>
      <FilterContent>
        <FilterItem>
          <DateFilter/>
        </FilterItem>
        <FilterItem>
          <EventFilter/>
        </FilterItem>
        <FilterItem style={{paddingTop: "10px"}}>
          <CheckboxContainer>
            <ShowFull/>
            <ShowPosition/>
          </CheckboxContainer>
        </FilterItem>
        <FilterItem>
          <Export/>
        </FilterItem>
      </FilterContent>
    </FilterContainer>
  )
}

interface DatefilterProps{
  fromDate: moment.Moment;
  toDate: moment.Moment;
  setDate: SetDate
}

const mapStateToDateProps = ({ txLog: { transaction: { fromDate, toDate }} }: AppState) => {
  return {
    fromDate,
    toDate
  }
}

const mapDispatchToDateProps = {
  setDate
}

const DateFilter = connect(mapStateToDateProps, mapDispatchToDateProps)(({fromDate, setDate, toDate}: DatefilterProps) => {

  const { formatMessage } = useIntl();

  const [ expandFilter, setExpandFilter ] = React.useState(true);

  const toggle = () => {
    setExpandFilter(!expandFilter);
  }

  return(
    <>
    <ItemHeader onClick={toggle}>
      {formatMessage({id: "date", defaultMessage: "Dato:"})}
      {expandFilter ? <ArrowDown/> : <ArrowUp/>}
    </ItemHeader>
    <Collapse isOpened={expandFilter}>
    <div>
      <label style={{textTransform: "capitalize"}} htmlFor="from-date">{formatMessage({id: "from", defaultMessage: "Fra"})}</label>
      <div>
        <StyledDateInput 
          id="from-date"
          onChange={(e) => {
              const date: Moment = moment(e.target.value)
              setDate({
                fromDate: date,
                toDate
              });
          }}
          value={fromDate.format("YYYY-MM-DDTHH:mm")} 
          aria-label="Date and time" 
          type="datetime-local" 
        />
      </div>
    </div>
    <div>
      <label style={{textTransform: "capitalize"}}  htmlFor="to-date">{formatMessage({id: "to", defaultMessage: "Til"})}</label>
      <div>
        <StyledDateInput 
          id="to-date"
          onChange={(e) => {
              const date: Moment = moment(e.target.value)
              setDate({
                fromDate,
                toDate: date
              });
          }}
          value={toDate.format("YYYY-MM-DDTHH:mm")} 
          aria-label="Date and time" 
          type="datetime-local" 
        />
      </div>
    </div>
    <CheckboxContainer style={{marginTop: "5px"}}>
      {transactionTimeFilterValues.map(value => {
        return(
          <HoverDiv 
            onClick={() => {
              let newDate = moment(new Date(new Date().getTime() - value.value * 60 * 60 * 1000));
              if(value.value >= 24){
                newDate.startOf("day");
              }
              setDate({
                fromDate: newDate,
                toDate: moment(new Date()).endOf("day")});
            }}
          >{formatMessage({id: value.label, defaultMessage: value.label})}</HoverDiv>
        );
      })}
    </CheckboxContainer>
    </Collapse>
    </>
  )
})

interface EventFilterProps{
  filter: TransactionEventFilterEnum[];
  setFilter: SetFilter;
  dehaId: number;
}

const mapStateToEventFilterProps = ({ txLog: { transaction: { filter }, device: { device: { dehaId }} } }: AppState) => {
  return {
    filter,
    dehaId
  }
}

const mapDispatchToEventFilterProps = {
  setFilter
}

const EventFilter = connect(mapStateToEventFilterProps, mapDispatchToEventFilterProps)(({ filter, setFilter, dehaId }: EventFilterProps) => {

  const { formatMessage } = useIntl();

  const [ expandFilter, setExpandFilter ] = React.useState(true);

  const toggle = () => {
    setExpandFilter(!expandFilter);
  }

  const toggleFilter = (value: TransactionEventFilterEnum) => {
    if(filter.includes(value)){
      setFilter(filter.filter(f => f !== value));
    }
    else{
      setFilter([value, ...filter]);
    }
  }

  return(
    <>
      <ItemHeader onClick={toggle}>
        {formatMessage({id: "eventFilter", defaultMessage: "Event filter:"})}
        {expandFilter ? <ArrowDown/> : <ArrowUp/>}
      </ItemHeader>
      <Collapse isOpened={expandFilter}>
        <CheckboxContainer>
        {transactionEventFilterValues.map(value => {
          if(value.value === TransactionEventFilterEnum.PASSING){
            if(!isStationary(dehaId)) return null;
          }
          return(
            <Checkbox
              name={value.label}
              id={value.label}
              label={formatMessage({id: value.label, defaultMessage: value.label})}
              value={filter.includes(value.value)}
              onChange={() => toggleFilter(value.value)}
            />
          )
        })}
       </CheckboxContainer>
      </Collapse>
    </>
  )
})

const HoverDiv = styled.div`
  border: 1px solid #ddd;
  border-radius: 6px;
  width: 181px;
  padding: 5px 9px;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
    transform: translateY(-1px);
  }
`;

const TimeFilter = connect(mapStateToDateProps, mapDispatchToDateProps)(({setDate, toDate}: DatefilterProps) => {

  const { formatMessage } = useIntl();

  return(
    <CheckboxContainer style={{marginTop: "5px"}}>
      {transactionTimeFilterValues.map(value => {
        return(
          <HoverDiv 
            onClick={() => {
              let newDate = moment(new Date(new Date().getTime() - value.value * 60 * 60 * 1000));
              if(value.value >= 24){
                newDate.startOf("day");
              }
              setDate({
                fromDate: newDate,
                toDate});
            }}
          >{formatMessage({id: value.label, defaultMessage: value.label})}</HoverDiv>
        );
      })}
    </CheckboxContainer>
  )
})

interface ShowFullProps{
  details: boolean;
  setDetails: SetDetails;
}

const mapStateToShowFullProps = ({ txLog: { transaction: { details }} }: AppState) => {
  return {
    details
  }
}

const mapDispatchToShowFullProps = {
  setDetails
}

const ShowFull = connect(mapStateToShowFullProps, mapDispatchToShowFullProps)(({ details, setDetails }: ShowFullProps) => {

  const { formatMessage } = useIntl();

  return(
    <Checkbox
      outerDiv={{
        alignItems: "center",
        gap: "5px"
      }}
      name="details"
      id="details"
      label={formatMessage({id: "showFullTransactionLog", defaultMessage: "Se fullstendig transaksjonslogg"})}
      value={details}
      onChange={() => setDetails(!details)}
    />
  )
})

interface ShowPositionProps{
  showPosition: boolean;
  trackable: boolean;
  setShowPosition: SetShowPosition
}

const mapStateToShowPositionProps = ({ txLog: { device: { device: {trackable}}, transaction: { showPosition }} }: AppState) => {
  return {
    showPosition,
    trackable
  }
}

const mapDispatchToShowPositionProps = {
  setShowPosition
}

const ShowPosition = connect(mapStateToShowPositionProps, mapDispatchToShowPositionProps)(({ trackable, showPosition, setShowPosition }: ShowPositionProps) => {
  const { formatMessage } = useIntl();

  if(!trackable) return null;

  return(
    <Checkbox
      outerDiv={{
        alignItems: "center",
        gap: "5px"
      }}
      name="showPosition"
      id="showPosition"
      label={formatMessage({id: "showPositions", defaultMessage: "Se posisjoner"})}
      value={showPosition}
      onChange={() => setShowPosition(!showPosition)}
    />
  )
})

interface ExportProps{
  transactions: Transaction[];
  details: boolean;
  showPosition: boolean;
  dehaId: number;
  serialNumber: string;
}

const mapStateToExportProps = ({ txLog: { transaction: { transactions, details, showPosition }, device: { device: { dehaId, serialNumber }}} }: AppState) => {
  return {
    transactions,
    details,
    showPosition,
    dehaId,
    serialNumber
  }
}

const Export = connect(mapStateToExportProps)(({transactions, details, showPosition, dehaId, serialNumber}: ExportProps) => {

  const { formatMessage } = useIntl();

  const [ expandFilter, setExpandFilter ] = React.useState(false);

  const toggle = () => {
    setExpandFilter(!expandFilter);
  }

  const { exportData, exportStyle } = React.useMemo(() => {
    if(transactions.length === 0) return {exportData: [], exportStyle: []}
    return mapDataForExport(formatMessage, dehaId, transactions, details, showPosition);
  }, [transactions, details])
  
  if(transactions.length === 0) return null;

  const name = `${formatMessage({id: "transactions", defaultMessage: "Transactions"})}_${serialNumber}`;

  return(
    <>
      <ItemHeader onClick={toggle}>
        {formatMessage({id: "exportLog", defaultMessage: "Last ned logg:"})}
        {expandFilter ? <ArrowDown/> : <ArrowUp/>}
      </ItemHeader>
      <Collapse isOpened={expandFilter}>
        <React.Fragment>
          <TableExport type="csv" data={exportData} fileName={name} />
          <TableExport colWidth={exportStyle} type="pdf" data={exportData} fileName={name} />
        </React.Fragment>
      </Collapse>
    </>
  )
})

export default Filter;